/* eslint-disable @typescript-eslint/no-explicit-any */
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ColumnDef } from '@tanstack/react-table';
import FeatherIcon from 'feather-icons-react';
import { Offcanvas } from 'react-bootstrap';
import { useState, useMemo, useEffect } from 'react';
import { useQuery } from 'react-query';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import PageBreadcrumb from 'components/common/PageBreadcrumb';
import { PageBreadcrumbItem } from 'components/common/PageBreadcrumb';
import Button from 'components/base/Button';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTable from 'components/base/AdvanceTable';
import SupperModal from 'components/modals/SupperModal';
import productGroupService from 'service/productGroup';
import CreateChildProduct from './CreateChildProducts';
import SearchBox from 'components/common/SearchBox';
import AdvanceTableCPagination from 'components/base/AdvanceTableCPagination';
import { pageToOffset } from 'utils/pageToOffset';

export type ProductsType = {
  name: string;
  barcode: string;
};

const ChildTable = () => {
  const { t } = useTranslation();

  const { pathname } = useLocation();
  const parentId = pathname.split('/').pop();

  const [openModal, setOpenModal] = useState(false);
  const [openSupperModal, setOpenSupperModal] = useState(false);
  const [confirmToDelete, setConfirmToDelete] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [searchInputValue, setSearchInputValue] = useState<any>('');
  const [pageCount, setPageCount] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);

  //Get Product Search
  const { data, refetch, isFetching, isLoading } = useQuery(
    ['GET_GROUP_PRODUCT_CHILD', parentId, currentPage, searchInputValue],
    () => {
      if (parentId) {
        const res = productGroupService
          .getProductGroup({
            offset: pageToOffset(currentPage, 10),
            limit: 10,
            parent_barcode: parentId,
            search: searchInputValue
          })
          .then((res: any) => {
            return res;
          });
        return res;
      }
    },
    {
      enabled:
        !!parentId ||
        !!currentPage ||
        !searchInputValue ||
        searchInputValue?.length > 2
    }
  );

  useEffect(() => {
    if (data) {
      setPageCount(Math.ceil(data?.count / 10));
    }
  }, [data]);

  const loading = isLoading || isFetching;

  const productsData: ProductsType[] = useMemo(() => {
    return (
      data?.group_products?.map((items: any) => {
        return {
          barcode: items?.child?.barcode,
          name: items?.child?.name
        };
      }) ?? []
    );
  }, [data]);

  const productsBreadcrumbItems: PageBreadcrumbItem[] = [
    {
      label: t('group_product'),
      url: '/products'
    },
    {
      label: parentId ?? '',
      active: true
    }
  ];

  const handleDelete = (id: string) => {
    setSelectedId(id);
    setOpenSupperModal(true);
  };

  const productsDataTableColumns: ColumnDef<ProductsType>[] = [
    {
      id: 'action',
      cell: rowData => {
        return (
          <div>
            <Button
              variant="hover"
              className="p-2"
              onClick={() => handleDelete(rowData?.row?.original?.barcode)}
            >
              <FeatherIcon
                icon="trash-2"
                className="cursor-pointer text-danger"
                size={20}
              />
            </Button>
          </div>
        );
      },
      meta: {
        headerProps: { style: { width: '7%' } },
        cellProps: { className: 'text-center' }
      }
    },
    {
      accessorKey: 'order_number',
      header: '№',
      cell: rowData => {
        const orderNumber = rowData.row.index + 1 + (currentPage - 1) * 10;
        return <div>{orderNumber}</div>;
      },
      meta: {
        headerProps: { style: { width: '5%' } },
        cellProps: { className: 'text-900 p-2' }
      }
    },
    {
      accessorKey: 'name',
      header: t('product_names'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'barcode',
      header: t('barcode'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    }
  ];

  const table = useAdvanceTable({
    data: productsData,
    columns: productsDataTableColumns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  useEffect(() => {
    if (confirmToDelete && selectedId) {
      productGroupService.deleteProductGroup(selectedId).finally(() => {
        refetch();
        setOpenSupperModal(false);
        setConfirmToDelete(false);
        setSelectedId(null);
      });
    }
  }, [confirmToDelete, selectedId, refetch]);

  return (
    <div>
      <PageBreadcrumb items={productsBreadcrumbItems} />

      <h2 className="mb-5">{parentId}</h2>
      <div className="d-flex justify-content-between align-items-center">
        <SearchBox
          placeholder={t('search')}
          value={searchInputValue}
          onChange={({ target }) => {
            setSearchInputValue(target.value);
          }}
        />

        <Button
          style={{ height: '48px' }}
          variant="primary"
          onClick={() => setOpenModal(true)}
        >
          <FontAwesomeIcon icon={faPlus} className="me-2" />
          {t('add')}
        </Button>
      </div>

      <AdvanceTableProvider {...table}>
        <div className="mt-4 mx-n4 px-4 mx-lg-n6 px-lg-6 bg-white border-top border-bottom border-300 position-relative top-1">
          <AdvanceTable
            tableProps={{ className: 'phoenix-table fs-9' }}
            isLoading={loading}
          />
          <AdvanceTableCPagination
            count={pageCount}
            page={currentPage}
            setCurrentPage={setCurrentPage}
          />
        </div>
      </AdvanceTableProvider>

      <SupperModal
        show={openSupperModal}
        setShow={setOpenSupperModal}
        setConfirm={setConfirmToDelete}
        title={t('products')}
        bodyText={t('you_want_delete')}
      />

      <Offcanvas
        show={openModal}
        onHide={() => {
          setOpenModal(false), refetch();
        }}
        placement="end"
        backdrop="static"
        keyboard={false}
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title style={{ fontSize: '24px', fontWeight: '600' }}>
            {t('add')}
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <CreateChildProduct parentId={parentId} />
        </Offcanvas.Body>
      </Offcanvas>
    </div>
  );
};

export default ChildTable;
